import { FCX } from "@datassential/platform-ui-lib";
import HomeHeader from "@pages/Home/components/HomeHeader/HomeHeader";
import SelectShow from "@pages/Home/components/HomeHeader/SelectShow";
import HomeSubHeader from "@pages/Home/components/HomeHeader/HomeSubHeader";
import SelectAmong, { SelectAmongWithContext } from "@vms/SelectAmong";
import { HomeShow } from "@core/constants";
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useHomeContext, useHomeSearchSettingsContext } from "@pages/Home/HomeContext";
import { DEFAULT_SELECT_AMONG_DATA, SelectAmongData, useSelectAmongContext } from "@vms/SelectAmong/SelectAmongContext";
import { AmongPayload } from "@vms/SelectAmong/useGetAmongPayload";
import {
    HomeTableIndexDataItem,
    HomeTableIndexRequestPayload,
    useHomeTableIndexData
} from "@api/useHomeTableIndexData";
import { useFilteredKeywordsData } from "@api/useFilteredKeywordsData";
import { useFilterSort } from "@hooks/useFilterSort";
import { Sort } from "@models";
import TableSortingContext, { TableSortingContextType } from "@components/Table/TableSorting";
import { HOME_INDEX_METRICS_ORDER } from "@models/Metric";
import { CopyTableOptions, useCopyTableDataWithFilters } from "@vms/CopyTable/useCopyTableDataWithFilters";
import { TableColumnsContext } from "@components/Table/TableColumns";
import WindowScrollerTable from "@components/Table/WindowScrollerTable";
import AsideButtons, { CopyTableButton } from "@components/AsideButtons";
import {
    TableViewIndexTableConfigProps,
    useTableViewIndexTableConfig
} from "@pages/Home/components/TableView/TableViewIndexTableConfig";
import ContentWidget from "@components/ContentWidget/ContentWidget";
import { useGetSelectedOptionsNames } from "@vms/SelectAmong/useSelectedOptionsNames";
import { RESPONDENTS_TYPES_DESCRIPTION, RespondentType } from "@models/RespondentType";
import { ProfileExportPptRequest } from "@pages/Profile/components/Header/Header";
import HomeActionsBar from "@pages/Home/components/HomeActionsBar/HomeActionsBar";

const TableViewIndex: FCX<{
    show: HomeShow;
    setShow: Dispatch<SetStateAction<HomeShow>>;
}> = ({
    show,
    setShow,
}) => {
    const [indexPayload, setIndexPayload] = useState<AmongPayload>();
    const [indexCopyTable, setIndexCopyTable] = useState<string | null>();
    const [indexData, setIndexData] = useState<SelectAmongData>(DEFAULT_SELECT_AMONG_DATA);

    const { search } = useHomeContext();
    const { data: homeSearchData, resetSearch } = useHomeSearchSettingsContext();
    const { amongPayload, data: amongData } = useSelectAmongContext();

    const payload = useMemo(
        (): HomeTableIndexRequestPayload => {
            return {
                mainModel: amongPayload,
                indexToModel: indexPayload
            };
        },
        [amongPayload, indexPayload],
    );

    const {
        isLoading,
        data: rawData,
    } = useHomeTableIndexData(payload);

    const { getFinalData, dataForSorting } = useFilteredKeywordsData(rawData, search);

    const mappedDataForSorting = useMemo(
        () => dataForSorting.map(item => ({
            ...item,
            ...item.metrics.reduce(
                (acc, metric, index) => ({
                    ...acc,
                    [`metric${index}`]: metric.sortValue,
                }),
                {}
            ),
        })),
        [dataForSorting]
    );

    const {
        data: sortedData,
        sortingKey,
        sortingDirection,
        setSortingKey,
        setSortingDirection,
    } = useFilterSort(
        mappedDataForSorting,
        'name',
        Sort.Asc,
    );

    useEffect(() => {
        if (homeSearchData?.sortByMetric) {
            const metricIndex = HOME_INDEX_METRICS_ORDER.findIndex(i => i === homeSearchData.sortByMetric);

            setSortingDirection(homeSearchData.sortingDirection);
            setSortingKey(`metric${metricIndex}` as keyof HomeTableIndexDataItem);
        }
    }, [homeSearchData, setSortingDirection, setSortingKey]);

    const data = useMemo(
        () => getFinalData(sortedData),
        [getFinalData, sortedData]
    );

    const tableConfigProps = useMemo(
        (): TableViewIndexTableConfigProps => ({
            data,
            search,
        }),
        [data, search],
    );

    const tableConfig = useTableViewIndexTableConfig(tableConfigProps);

    const tableProviderValue = useMemo(
        () => ({
            data: tableConfig,
        }),
        [tableConfig],
    );

    const tableSortingProviderValue = useMemo(
        (): TableSortingContextType => ({
            value: {
                id: sortingKey,
                direction: sortingDirection,
            },
            options: [
                { id: 'name', name: 'name', defaultDirection: Sort.Asc, },
                { id: 'macStage', name: 'macStage', },
                { id: 'base', name: 'base', },
                ...HOME_INDEX_METRICS_ORDER.map((_, index) => ({
                    id: `metric${index}`,
                    name: `metric${index}`,
                })),
            ],
            setValue: ({ id, direction }) => {
                setSortingKey(id as keyof HomeTableIndexDataItem);
                setSortingDirection(direction);
            },
        }),
        [sortingKey, sortingDirection, setSortingKey, setSortingDirection]
    );

    const copyTableOptions = useMemo(
        (): CopyTableOptions => ({
            additionalRows: [[`Index To: ${indexCopyTable ?? ""}`]],
        }),
        [indexCopyTable]
    );

    const copyTableContent = useCopyTableDataWithFilters(
        tableConfig,
        data.length,
        copyTableOptions,
    );

    const getSelectedOptionsNames = useGetSelectedOptionsNames(true);

    const getAmongDescription = useCallback((data: SelectAmongData) => {
        const amongNames = getSelectedOptionsNames(data);
        return amongNames.length ? amongNames.join(', ') : RESPONDENTS_TYPES_DESCRIPTION[RespondentType.TotalUS]
    }, [getSelectedOptionsNames])

    const amongDescription = useMemo(() => getAmongDescription(amongData), [amongData, getAmongDescription])
    const indexDescription = useMemo(() => getAmongDescription(indexData), [indexData, getAmongDescription])

    const exportPayload = useMemo(
        (): ProfileExportPptRequest => ({
            ...amongPayload,
        }),
        [amongPayload]
    );

    const indexInitialData = useMemo(
        () => {
            return homeSearchData?.indexTo;
        },
        [homeSearchData]
    );

    return (
        <>
            <HomeHeader resultCount={data.length}>
                <SelectShow show={show} setShow={setShow}/>
            </HomeHeader>
            <HomeSubHeader>
                <SelectAmong
                    label="AMONG:"
                    useGroupsLimit
                    useCoExclusions
                    onApply={resetSearch}
                />
                <SelectAmongWithContext
                    label="INDEX TO:"
                    fieldClassName="appears-left"
                    onPayloadChange={setIndexPayload}
                    onCopyTableTextChange={setIndexCopyTable}
                    onDataChange={setIndexData}
                    useGroupsLimit
                    useCoExclusions
                    initialData={indexInitialData}
                    onApply={resetSearch}
                />
            </HomeSubHeader>
            <TableSortingContext.Provider value={tableSortingProviderValue}>
                <TableColumnsContext.Provider value={tableProviderValue}>
                    <div className="HomeTableViewIndex__container">
                        <WindowScrollerTable
                            className="HomeTableViewIndex__table"
                            rowsCount={isLoading ? 0 : data.length}
                            headHeight={48}
                            rowHeight={42}
                            isLoading={isLoading}
                            showFallback
                        >
                            <AsideButtons>
                                <CopyTableButton
                                    data={copyTableContent}
                                    testId="buttonHistoricTrendCopyTable"
                                />
                            </AsideButtons>
                        </WindowScrollerTable>
                        <ContentWidget className="HomeTableViewIndex__info-container">
                            <div className="HomeTableViewIndex__info-text">
                                <div className="HomeTableViewIndex__caption">Indices shown</div>
                                <div className="HomeTableViewIndex__text">
                                    <span>Figures above 100 indicate items that</span>
                                    <span className="HomeTableViewIndex__highlighted-text">{amongDescription}</span>
                                    <span>are more likely to know, have tried, or like compared to</span>
                                    <span className="HomeTableViewIndex__highlighted-text">{indexDescription}</span>
                                    <span>.</span>
                                </div>
                            </div>
                        </ContentWidget>
                    </div>
                </TableColumnsContext.Provider>
            </TableSortingContext.Provider>
            <HomeActionsBar exportPayload={exportPayload}/>
        </>
    );
};

export default TableViewIndex;